import { createContext, useContext, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { AUTH_EVENT, api } from "./api";

type Me = { user: string; auth: boolean };

const MeContext = createContext<{ me: Me; setMe: (me: Me) => void } | null>(null);
export const useMe = () => useContext(MeContext);

// Oturum yoksa giriş ekranı, varsa uygulama
export function AuthGate({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(undefined);

  useEffect(() => {
    const check = () =>
      fetch("/api/me")
        .then(r => (r.ok ? r.json() : null))
        .then(setMe)
        .catch(() => setMe(null));
    check();
    const onExpired = () => setMe(null);
    window.addEventListener(AUTH_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EVENT, onExpired);
  }, []);

  if (me === undefined) return null;
  if (!me) return <Login onDone={setMe} />;
  return <MeContext.Provider value={{ me, setMe }}>{children}</MeContext.Provider>;
}

// Kullanıcı adı / şifre değiştirme (Hesap modalı içinde)
export function AccountPanel({ onDone }: { onDone: () => void }) {
  const ctx = useMe();
  const [username, setUsername] = useState(ctx?.me.user ?? "");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!ctx?.me.auth) {
    return <p className="text-sm text-slate-500">Yerel geliştirmede giriş kapalı; hesap ayarı sadece canlı sunucuda var.</p>;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    if (next !== repeat) {
      setStatus({ ok: false, text: "Yeni şifreler eşleşmiyor." });
      return;
    }
    setBusy(true);
    try {
      const res = await api<{ user: string }>("/api/account", {
        method: "POST",
        body: JSON.stringify({ username, currentPassword: current, newPassword: next }),
      });
      ctx.setMe({ ...ctx.me, user: res.user });
      setCurrent("");
      setNext("");
      setRepeat("");
      setStatus({ ok: true, text: "Kaydedildi. Diğer cihazlardaki oturumlar kapatıldı." });
      setTimeout(onDone, 1200);
    } catch (err) {
      setStatus({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {status &&
        (status.ok ? (
          <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{status.text}</p>
        ) : (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{status.text}</p>
        ))}
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Kullanıcı adı</span>
        <input
          required
          autoComplete="username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          className="input"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Yeni şifre</span>
        <input
          type="password"
          autoComplete="new-password"
          minLength={12}
          placeholder="Değiştirmeyecekseniz boş bırakın"
          value={next}
          onChange={e => setNext(e.target.value)}
          className="input"
        />
      </label>
      {next && (
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Yeni şifre (tekrar)</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={e => setRepeat(e.target.value)}
            className="input"
          />
        </label>
      )}
      <label className="block border-t border-slate-100 pt-4">
        <span className="mb-1 block text-sm font-medium text-slate-700">Mevcut şifre</span>
        <input
          required
          data-autofocus
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={e => setCurrent(e.target.value)}
          className="input"
        />
        <span className="mt-1 block text-xs text-slate-500">Değişikliği onaylamak için gerekli.</span>
      </label>
      <div className="flex justify-end">
        <button type="submit" disabled={busy} className="btn-primary">
          {busy ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </form>
  );
}

function Login({ onDone }: { onDone: (me: Me) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Giriş — Çalışma Raporu";
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ user: string }>("/api/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      onDone({ user: res.user, auth: true });
    } catch (err) {
      setError((err as Error).message);
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="grid size-9 place-items-center rounded-lg bg-blue-700 text-sm font-bold text-white">R</div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">Çalışma Raporu</h1>
            <p className="text-sm text-slate-500">Devam etmek için giriş yapın</p>
          </div>
        </div>
        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        )}
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Kullanıcı adı</span>
          <input
            required
            autoFocus
            autoComplete="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            className="input"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Şifre</span>
          <input
            required
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="input"
          />
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? "Giriş yapılıyor…" : "Giriş yap"}
        </button>
      </form>
    </div>
  );
}
